import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { showGlobalNotification } from '../hooks/useNotifications';
import { apiService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  Plus, Search, Edit2, Trash2, Wifi, Activity, AlertTriangle, 
  CheckCircle, Clock, Thermometer, Droplets,
  Building2, MapPin, User, Calendar, Settings, Eye, Camera,
  Monitor, Bluetooth, Radio, Gauge, Zap, Router, Copy, X, QrCode, Printer
} from 'lucide-react';
import { IoTDevice } from '../types';
import Modal from './Modal';

const IoTDevicesPage: React.FC = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState<IoTDevice | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<IoTDevice | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterProtocol, setFilterProtocol] = useState<string>('all');
  const [devicesData, setDevicesData] = useState<IoTDevice[]>([]);
  const [companiesData, setCompaniesData] = useState<any[]>([]);
  const [locationsData, setLocationsData] = useState<any[]>([]);
  const [usersData, setUsersData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>('');
  
  // Toplu etiket state'leri
  const [showBulkLabelModal, setShowBulkLabelModal] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState<number[]>([]);
  const [bulkLabelFilters, setBulkLabelFilters] = useState({
    location: 'all',
    status: 'all',
    type: 'all'
  });
  const [filteredDevicesForLabel, setFilteredDevicesForLabel] = useState<IoTDevice[]>([]);
  const [showBulkLabelPreview, setShowBulkLabelPreview] = useState(false);
  
  const [deviceData, setDeviceData] = useState({
    device_name: '',
    device_type: 'sensor' as IoTDevice['device_type'],
    brand: '',
    model: '',
    serial_number: '',
    mac_address: '',
    ip_address: '',
    firmware_version: '',
    location_id: 0,
    company_id: 0,
    installation_date: '',
    communication_protocol: 'wifi' as IoTDevice['communication_protocol'],
    operating_system: '',
    status: 'offline' as IoTDevice['status'],
    assigned_admin_id: 0,
    warranty_end_date: '',
    purchase_price: 0,
    currency: 'TRY' as 'TRY' | 'USD' | 'EUR',
    notes: ''
  });

  // Load data on component mount
  React.useEffect(() => {
    loadData();
  }, []);

  // Bildirimden belirli bir IoT cihazı açma
  React.useEffect(() => {
    const handler = async (e: CustomEvent) => {
      if (e.detail?.page !== 'iot-devices' || !e.detail?.entityId) return;
      try {
        const device = await apiService.getIoTDeviceById(e.detail.entityId);
        if (device) { setSelectedDevice(device); setShowDetailModal(true); }
      } catch (err) { console.error('[openEntityById iotdevices]', err); }
    };
    window.addEventListener('openEntityById', handler as EventListener);
    return () => window.removeEventListener('openEntityById', handler as EventListener);
  }, []);

  // Toplu etiket modal'ı açıldığında filtrelenmiş listeyi başlat
  React.useEffect(() => {
    if (showBulkLabelModal) {
      setFilteredDevicesForLabel(devicesData);
    }
  }, [showBulkLabelModal, devicesData]);

  // Toplu etiket filtreleri değiştiğinde filtreyi uygula
  React.useEffect(() => {
    if (showBulkLabelModal) {
      applyBulkLabelFilters();
    }
  }, [bulkLabelFilters, devicesData, showBulkLabelModal]);

  const generateQRCode = (device: IoTDevice) => {
    const token = localStorage.getItem('auth_token');
    const params = new URLSearchParams({
      token: token || '',
    });
    const siteUrl = import.meta.env.VITE_SITE_URL || `${window.location.protocol}//${window.location.host}`;
    const url = `${siteUrl}/api/iot/${device.id}/qr-code?${params}`;
    
    // Yeni sekmede aç
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.location.href = url;
    } else {
      // Popup engellenmişse direkt URL'yi aç
      window.location.href = url;
    }
  };

  // Toplu etiket fonksiyonları
  const applyBulkLabelFilters = () => {
    let filtered = devicesData;

    if (bulkLabelFilters.location !== 'all') {
      filtered = filtered.filter(device => device.location_id === parseInt(bulkLabelFilters.location));
    }

    if (bulkLabelFilters.status !== 'all') {
      filtered = filtered.filter(device => device.status === bulkLabelFilters.status);
    }

    if (bulkLabelFilters.type !== 'all') {
      filtered = filtered.filter(device => device.device_type === bulkLabelFilters.type);
    }

    setFilteredDevicesForLabel(filtered);
  };

  const toggleDeviceSelection = (deviceId: number) => {
    setSelectedDevices(prev => 
      prev.includes(deviceId) 
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const selectAllDevices = () => {
    setSelectedDevices(filteredDevicesForLabel.map(device => device.id));
  };

  const deselectAllDevices = () => {
    setSelectedDevices([]);
  };

  const printBulkLabels = async () => {
    const selectedDeviceData = devicesData.filter(d => selectedDevices.includes(d.id));
    const token = localStorage.getItem('auth_token');

    // Get QR codes for all selected devices
    const qrCodes: { [key: number]: string } = {};
    for (const device of selectedDeviceData) {
      try {
        const siteUrl = import.meta.env.VITE_SITE_URL || `${window.location.protocol}//${window.location.host}`;
        const response = await fetch(`${siteUrl}/api/iot/${device.id}/qr-code?token=${token}&json=true`);
        if (response.ok) {
          const data = await response.json();
          qrCodes[device.id] = data.qrCode;
        }
      } catch (error) {
        console.error('Error fetching QR code for device', device.id, error);
      }
    }

    // Create print-friendly page
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const labelsPerPage = 4;
      const totalPages = Math.ceil(selectedDeviceData.length / labelsPerPage);
      
      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>${t('iot.bulkModalTitle')}</title>
          <style>
            @page { size: A4; margin: 10mm; }
            body { 
              font-family: Arial, sans-serif; 
              margin: 0; 
              padding: 0; 
              background: white;
            }
            .label-page {
              width: 100%;
              height: 100vh;
              display: grid;
              grid-template-columns: 1fr 1fr;
              grid-template-rows: 1fr 1fr;
              gap: 10mm;
              page-break-after: always;
            }
            .label {
              border: 2px solid #333;
              border-radius: 10px;
              padding: 15px;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              text-align: center;
              background: white;
            }
            .device-name {
              font-size: 26px;
              font-weight: bold;
              color: #2c3e50;
              margin-bottom: 10px;
            }
            .device-info {
              font-size: 14px;
              color: #666;
              margin-bottom: 5px;
            }
            .qr-code {
              margin-top: 10px;
            }
            .qr-code img {
              max-width: 80px;
              max-height: 80px;
            }
          </style>
        </head>
        <body>
      `;

      for (let page = 0; page < totalPages; page++) {
        htmlContent += '<div class="label-page">';
        
        for (let i = 0; i < labelsPerPage; i++) {
          const deviceIndex = page * labelsPerPage + i;
          if (deviceIndex < selectedDeviceData.length) {
            const device = selectedDeviceData[deviceIndex];
            const qrCode = qrCodes[device.id];
            
            htmlContent += `
              <div class="label">
                <div class="device-name">${device.device_name}</div>
                <div class="device-info">${device.brand} ${device.model}</div>
                <div class="device-info">${t('iot.printIpLabel')} ${device.ip_address || t('iot.printNotSpecified')}</div>
                ${qrCode ? `<div class="qr-code"><img src="${qrCode}" alt="QR Code" /></div>` : ''}
              </div>
            `;
          } else {
            htmlContent += '<div class="label"></div>';
          }
        }
        
        htmlContent += '</div>';
      }

      htmlContent += '</body></html>';
      
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Her API çağrısını ayrı ayrı yap ve hata yönetimi ekle
      let devices = [];
      let companies = [];
      let locations = [];
      let users = [];
      
      try {
        const devicesResponse = await apiService.getIoTDevices();
        devices = Array.isArray(devicesResponse) ? devicesResponse : [];
      } catch (error) {
        console.error('Error loading devices:', error);
      }
      
      try {
        const companiesResponse = await apiService.getCompanies();
        companies = Array.isArray(companiesResponse) ? companiesResponse : [];
      } catch (error) {
        console.error('Error loading companies:', error);
      }
      
      try {
        const locationsResponse = await apiService.getLocations();
        
        // Backend'den gelen response formatını kontrol et
        if (locationsResponse && locationsResponse.data) {
          locations = Array.isArray(locationsResponse.data) ? locationsResponse.data : [];
        } else if (Array.isArray(locationsResponse)) {
          locations = locationsResponse;
        } else {
          locations = [];
        }
      } catch (error) {
        console.error('Error loading locations:', error);
        showGlobalNotification.error(t('common.error'), t('iot.errorLocations'));
      }
      
      try {
        // Sadece admin/superadmin kullanıcıları için users endpoint'ini çağır
        if (['admin', 'superadmin'].includes(user?.role || '')) {
          const usersResponse = await apiService.getUsers();
          users = Array.isArray(usersResponse) ? usersResponse.filter(u => u.role === 'admin' || u.role === 'superadmin') : [];
        } else {
          users = [];
        }
      } catch (error) {
        console.error('Error loading users:', error);
      }
      
      // Lokasyon yükleme hatası kontrolü
      if (locations.length === 0) {
        console.warn('No locations loaded, this might cause issues in the form');
        showGlobalNotification.error(t('common.warning'), t('iot.warnLocations'));
      }
      
      setDevicesData(devices);
      setCompaniesData(companies);
      setLocationsData(locations);
      setUsersData(users);
    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error(t('common.error'), t('iot.errorData'));
    } finally {
      setLoading(false);
    }
  };

  const deviceTypeOptions = [
    { value: 'sensor', label: t('iot.typeSensor'), icon: Gauge },
    { value: 'camera', label: t('iot.typeCamera'), icon: Camera },
    { value: 'display', label: t('iot.typeDisplay'), icon: Monitor },
    { value: 'controller', label: t('iot.typeController'), icon: Settings },
    { value: 'gateway', label: t('iot.typeGateway'), icon: Router },
    { value: 'beacon', label: t('iot.typeBeacon'), icon: Radio },
    { value: 'tracker', label: t('iot.typeTracker'), icon: MapPin }
  ];

  const statusOptions = [
    { value: 'online', label: t('iot.statusOnline'), color: 'bg-green-100 text-green-800' },
    { value: 'offline', label: t('iot.statusOffline'), color: 'bg-red-100 text-red-800' },
    { value: 'maintenance', label: t('iot.statusMaintenance'), color: 'bg-yellow-100 text-yellow-800' },
    { value: 'faulty', label: t('iot.statusFaulty'), color: 'bg-red-100 text-red-800' }
  ];

  const healthOptions = [
    { value: 'healthy', label: t('iot.healthHealthy'), color: 'bg-green-100 text-green-800' },
    { value: 'warning', label: t('iot.healthWarning'), color: 'bg-yellow-100 text-yellow-800' },
    { value: 'critical', label: t('iot.healthCritical'), color: 'bg-red-100 text-red-800' },
    { value: 'unknown', label: t('iot.healthUnknown'), color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' }
  ];

  const protocolOptions = [
    { value: 'wifi', label: 'Wi-Fi', icon: Wifi },
    { value: 'bluetooth', label: 'Bluetooth', icon: Bluetooth },
    { value: 'zigbee', label: 'ZigBee', icon: Radio },
    { value: 'lora', label: 'LoRa', icon: Radio },
    { value: 'cellular', label: 'Cellular', icon: Radio },
    { value: 'ethernet', label: 'Ethernet', icon: Zap }
  ];

  const filteredDevices = devicesData.filter(device => {
    const matchesSearch = String(device.device_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         String(device.model || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         String(device.serial_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         device.ip_address?.includes(searchTerm);
    const matchesStatus = filterStatus === 'all' || device.status === filterStatus;
    const matchesType = filterType === 'all' || device.device_type === filterType;
    const matchesProtocol = filterProtocol === 'all' || device.communication_protocol === filterProtocol;
    
    return matchesSearch && matchesStatus && matchesType && matchesProtocol;
  });

  const handleEdit = (device: IoTDevice) => {
    setEditingDevice(device);
    setDeviceData({
      device_name: device.device_name,
      device_type: device.device_type,
      brand: device.brand,
      model: device.model,
      serial_number: device.serial_number,
      mac_address: device.mac_address || '',
      ip_address: device.ip_address || '',
      firmware_version: device.firmware_version || '',
      location_id: device.location_id,
      company_id: device.company_id,
      installation_date: device.installation_date ? new Date(device.installation_date).toISOString().split('T')[0] : '',
      communication_protocol: device.communication_protocol,
      operating_system: device.operating_system || '',
      status: device.status,
      assigned_admin_id: device.assigned_admin_id || 0,
      warranty_end_date: device.warranty_end_date || '',
      purchase_price: device.purchase_price || 0,
      currency: device.currency || 'TRY',
      notes: device.notes || ''
    });
    setImageFile(null);
    setShowModal(true);
    
  };

  const handleAdd = () => {
    setEditingDevice(null);
    setDeviceData({
      device_name: '',
      device_type: 'sensor',
      brand: '',
      model: '',
      serial_number: '',
      mac_address: '',
      ip_address: '',
      firmware_version: '',
      location_id: 0,
      company_id: 0,
      installation_date: '',
      communication_protocol: 'wifi',
      operating_system: '',
      status: 'offline',
      assigned_admin_id: 0,
      warranty_end_date: '',
      purchase_price: 0,
      currency: 'TRY',
      notes: ''
    });
    setImageFile(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!deviceData.device_name || !deviceData.brand || !deviceData.model || !deviceData.serial_number) {
      showGlobalNotification.error(t('common.error'), t('iot.errorRequired'));
      return;
    }
    
    try {
      const formData = new FormData();
      Object.keys(deviceData).forEach(key => {
        formData.append(key, deviceData[key as keyof typeof deviceData].toString());
      });
      
      if (imageFile) {
        formData.append('image', imageFile);
      }
      
      if (editingDevice) {
        await apiService.updateIoTDevice(editingDevice.id, formData);
        showGlobalNotification.success(t('common.success'), t('iot.successUpdated'));
      } else {
        await apiService.createIoTDevice(formData);
        showGlobalNotification.success(t('common.success'), t('iot.successCreated'));
      }

      await loadData();
      setShowModal(false);
    } catch (error: any) {
      console.error('Error saving IoT device:', error);
      showGlobalNotification.error(t('common.error'), error.message || t('iot.errorSave'));
    }
  };

  const handleDelete = async (id: number) => {
    const deviceToDelete = devicesData.find(d => d.id === id);
    if (!deviceToDelete) return;
    
    if (confirm(t('iot.confirmDelete'))) {
      try {
        await apiService.deleteIoTDevice(id);
        await loadData();
        showGlobalNotification.success(t('common.success'), t('iot.successDeleted'));
      } catch (error: any) {
        console.error('Error deleting IoT device:', error);
        showGlobalNotification.error(t('common.error'), error.message || t('iot.errorDelete'));
      }
    }
  };

  const showDeviceDetail = (device: IoTDevice) => {
    setSelectedDevice(device);
    setShowDetailModal(true);
  };

  const handleCopy = (device: IoTDevice) => {
    setEditingDevice(null);
    setDeviceData({
      device_name: '', // Cihaz adı kopyalanmasın
      device_type: device.device_type,
      brand: device.brand,
      model: device.model,
      serial_number: '', // Seri no kopyalanmasın
      mac_address: '', // MAC adresi kopyalanmasın
      ip_address: '', // IP adresi kopyalanmasın
      firmware_version: device.firmware_version || '',
      location_id: device.location_id,
      company_id: device.company_id,
      installation_date: device.installation_date ? new Date(device.installation_date).toISOString().split('T')[0] : '', // Kurulum tarihi kopyalansın
      communication_protocol: device.communication_protocol,
      operating_system: device.operating_system || '',
      status: 'offline',
      assigned_admin_id: device.assigned_admin_id || 0,
      warranty_end_date: device.warranty_end_date ? new Date(device.warranty_end_date).toISOString().split('T')[0] : '', // Garanti bitiş tarihi kopyalansın
      purchase_price: device.purchase_price || 0,
      currency: device.currency || 'TRY',
      notes: device.notes || ''
    });
    setImageFile(null);
    setShowModal(true);
    showGlobalNotification.info(t('common.info'), t('iot.infoCopied'));
  };

  const deviceAction = (device: IoTDevice, action: 'restart' | 'update' | 'calibrate') => {
    // Here would be the API call to perform device actions
  };

  const getStatusColor = (status: string) => {
    return statusOptions.find(s => s.value === status)?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };


  const getDeviceTypeIcon = (type: string) => {
    return deviceTypeOptions.find(t => t.value === type)?.icon || Settings;
  };

  const getProtocolIcon = (protocol: string) => {
    return protocolOptions.find(p => p.value === protocol)?.icon || Wifi;
  };

  const getCurrencySymbol = (currency: string) => {
    switch (currency) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'TRY': return '₺';
      default: return '₺';
    }
  };


  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('iot.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">{t('iot.subtitle')}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <button
            onClick={() => setShowBulkLabelModal(true)}
            className="flex items-center justify-center px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors min-h-[44px] text-sm"
          >
            <QrCode className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">{t('iot.bulkLabel')}</span>
            <span className="sm:hidden">{t('iot.label')}</span>
          </button>
          <button
            onClick={handleAdd}
            className="flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors min-h-[44px] text-sm sm:text-base"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('iot.newDevice')}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('iot.statTotal')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{devicesData.length}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Wifi className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('iot.statOnline')}</p>
              <p className="text-2xl font-bold text-green-600">
                {devicesData.filter(d => d.status === 'online').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('iot.statOffline')}</p>
              <p className="text-2xl font-bold text-red-600">
                {devicesData.filter(d => d.status === 'offline').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('iot.statWarning')}</p>
              <p className="text-2xl font-bold text-yellow-600">
                {devicesData.filter(d => d.health_status === 'warning').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder={t('iot.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent w-full"
          />
        </div>
        
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
        >
          <option value="all">{t('iot.filterAllTypes')}</option>
          {deviceTypeOptions.map(type => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>

        <select
          value={filterProtocol}
          onChange={(e) => setFilterProtocol(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
        >
          <option value="all">{t('iot.filterAllProtocols')}</option>
          {protocolOptions.map(protocol => (
            <option key={protocol.value} value={protocol.value}>{protocol.label}</option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
        >
          <option value="all">{t('iot.filterAllStatuses')}</option>
          {statusOptions.map(status => (
            <option key={status.value} value={status.value}>{status.label}</option>
          ))}
        </select>
      </div>

      {/* Devices Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('iot.colDevice')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('iot.colType')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('iot.colCompany')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('iot.colLocation')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('iot.colIp')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('iot.colStatus')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('iot.colActions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredDevices.map((device) => {
                const TypeIcon = getDeviceTypeIcon(device.device_type);
                return (
                  <tr key={device.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                          <TypeIcon className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {device.device_name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {device.brand} {device.model}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 dark:text-gray-100 capitalize">
                        {deviceTypeOptions.find(t => t.value === device.device_type)?.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Building2 className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900 dark:text-gray-100">{device.company?.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900 dark:text-gray-100">{device.location?.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 dark:text-gray-100">
                        {device.ip_address || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(device.status)}`}>
                        {statusOptions.find(s => s.value === device.status)?.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => showDeviceDetail(device)}
                          className="p-1 text-gray-400 hover:text-primary transition-colors"
                          title={t('iot.titleView')}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleCopy(device)}
                          className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                          title={t('iot.titleCopy')}
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => generateQRCode(device)}
                          className="p-1 text-gray-400 hover:text-purple-600 transition-colors"
                          title={t('iot.titleQr')}
                        >
                          <QrCode className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(device)}
                          className="p-1 text-gray-400 hover:text-primary transition-colors"
                          title={t('iot.titleEdit')}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(device.id)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title={t('iot.titleDelete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Device Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[95vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editingDevice ? t('iot.modalTitleEdit') : t('iot.modalTitleNew')}
              </h2>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('iot.sectionBasic')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('iot.labelDeviceName')}
                    </label>
                    <input
                      type="text"
                      value={deviceData.device_name}
                      onChange={(e) => setDeviceData({...deviceData, device_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="TEMP-SENSOR-01"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('iot.labelDeviceType')}
                    </label>
                    <select
                      value={deviceData.device_type}
                      onChange={(e) => setDeviceData({...deviceData, device_type: e.target.value as IoTDevice['device_type']})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      {deviceTypeOptions.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('iot.labelBrand')}
                    </label>
                    <input
                      type="text"
                      value={deviceData.brand}
                      onChange={(e) => setDeviceData({...deviceData, brand: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Bosch"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('iot.labelModel')}
                    </label>
                    <input
                      type="text"
                      value={deviceData.model}
                      onChange={(e) => setDeviceData({...deviceData, model: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="BME280"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('iot.labelSerialNumber')}
                    </label>
                    <input
                      type="text"
                      value={deviceData.serial_number}
                      onChange={(e) => setDeviceData({...deviceData, serial_number: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="BME280-2024-001"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('iot.labelMacAddress')}
                    </label>
                    <input
                      type="text"
                      value={deviceData.mac_address}
                      onChange={(e) => setDeviceData({...deviceData, mac_address: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="AA:BB:CC:DD:EE:01"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('iot.labelIpAddress')}
                    </label>
                    <input
                      type="text"
                      value={deviceData.ip_address}
                      onChange={(e) => setDeviceData({...deviceData, ip_address: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="192.168.100.10"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('iot.labelFirmware')}
                    </label>
                    <input
                      type="text"
                      value={deviceData.firmware_version}
                      onChange={(e) => setDeviceData({...deviceData, firmware_version: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="v2.1.3"
                    />
                  </div>
                </div>
              </div>

              {/* Communication and Configuration */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('iot.sectionComm')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('iot.labelProtocol')}
                    </label>
                    <select
                      value={deviceData.communication_protocol}
                      onChange={(e) => setDeviceData({...deviceData, communication_protocol: e.target.value as IoTDevice['communication_protocol']})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      {protocolOptions.map((protocol) => (
                        <option key={protocol.value} value={protocol.value}>
                          {protocol.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('iot.labelOs')}
                    </label>
                    <input
                      type="text"
                      value={deviceData.operating_system}
                      onChange={(e) => setDeviceData({...deviceData, operating_system: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="e.g. Linux, Windows IoT, FreeRTOS"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('iot.labelStatus')}
                    </label>
                    <select
                      value={deviceData.status}
                      onChange={(e) => setDeviceData({...deviceData, status: e.target.value as IoTDevice['status']})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      {statusOptions.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('iot.labelInstallDate')}
                    </label>
                    <input
                      type="date"
                      value={deviceData.installation_date}
                      onChange={(e) => setDeviceData({...deviceData, installation_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Location and Assignment */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('iot.sectionLocation')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('iot.labelCompany')}
                    </label>
                    <select
                      value={deviceData.company_id}
                      onChange={(e) => setDeviceData({...deviceData, company_id: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value={0}>{t('iot.selectCompany')}</option>
                     {companiesData.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('iot.labelLocation')}
                    </label>
                    <select
                      value={deviceData.location_id}
                      onChange={(e) => {
                        const locationId = parseInt(e.target.value);
                        console.log('Location changed to:', locationId);
                        setDeviceData({...deviceData, location_id: locationId});
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value={0}>{t('iot.selectLocation')}</option>
                     {locationsData.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                    {locationsData.length === 0 && (
                      <p className="text-xs text-red-500 mt-1">{t('iot.locationsLoadError')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('iot.labelAdmin')}
                    </label>
                    <select
                      value={deviceData.assigned_admin_id}
                      onChange={(e) => setDeviceData({...deviceData, assigned_admin_id: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value={0}>{t('iot.selectAdmin')}</option>
                     {usersData.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('iot.labelWarrantyEnd')}
                    </label>
                    <input
                      type="date"
                      value={deviceData.warranty_end_date}
                      onChange={(e) => setDeviceData({...deviceData, warranty_end_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Purchase Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('iot.sectionPurchase')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('iot.labelPurchasePrice')}
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="number"
                        step="0.01"
                        value={deviceData.purchase_price}
                        onChange={(e) => setDeviceData({...deviceData, purchase_price: parseFloat(e.target.value) || 0})}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="150"
                      />
                      <select
                        value={deviceData.currency}
                        onChange={(e) => setDeviceData({...deviceData, currency: e.target.value as 'TRY' | 'USD' | 'EUR'})}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="TRY">₺</option>
                        <option value="USD">$</option>
                        <option value="EUR">€</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('iot.labelDeviceImage')}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      // Dosya validasyonu
                      const maxSize = 5 * 1024 * 1024; // 5MB
                      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
                      
                      if (file.size > maxSize) {
                        showGlobalNotification.error(t('common.error'), t('iot.errorImageSize'));
                        e.target.value = '';
                        return;
                      }

                      if (!allowedTypes.includes(file.type)) {
                        showGlobalNotification.error(t('common.error'), t('iot.errorImageType'));
                        e.target.value = '';
                        return;
                      }

                      setImageFile(file);
                      showGlobalNotification.success(t('common.success'), t('iot.successFileSelected'));
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('iot.imageHint')}</p>
                {imageFile && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-sm text-green-800">
                      <strong>{t('iot.imageSelectedFile')}</strong> {imageFile.name} ({(imageFile.size / 1024 / 1024).toFixed(2)} MB)
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('iot.labelNotes')}
                </label>
                <textarea
                  value={deviceData.notes}
                  onChange={(e) => setDeviceData({...deviceData, notes: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder={t('iot.notesPlaceholder')}
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                {editingDevice ? t('iot.update') : t('common.save')}
              </button>
            </div>
          </div>
        </Modal>

      {/* Device Detail Modal */}
      <Modal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)}>
          {selectedDevice && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('iot.detailTitle')}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {selectedDevice.device_name} - {selectedDevice.brand} {selectedDevice.model}
                  </p>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Device Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('iot.detailSectionDevice')}</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('iot.detailDeviceName')}</span>
                      <span className="font-medium">{selectedDevice.device_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('iot.detailType')}</span>
                      <span className="font-medium capitalize">
                        {deviceTypeOptions.find(opt => opt.value === selectedDevice.device_type)?.label}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('iot.detailBrandModel')}</span>
                      <span className="font-medium">{selectedDevice.brand} {selectedDevice.model}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('iot.detailSerialNo')}</span>
                      <span className="font-medium">{selectedDevice.serial_number}</span>
                    </div>
                    {selectedDevice.firmware_version && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('iot.detailFirmware')}</span>
                        <span className="font-medium">{selectedDevice.firmware_version}</span>
                      </div>
                    )}
                  </div>
                </div>

                {selectedDevice.image_path && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('iot.detailSectionImage')}</h3>
                    <div className="flex justify-center">
                      <img
                        src={`${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3004'}${selectedDevice.image_path}`}
                        alt={selectedDevice.device_name}
                        className="max-w-xs max-h-48 object-contain rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => {
                          setSelectedImage(`${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3004'}${selectedDevice.image_path}`);
                          setShowImageModal(true);
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('iot.detailSectionNetwork')}</h3>
                  <div className="space-y-3 text-sm">
                    {selectedDevice.ip_address && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('iot.detailIpAddress')}</span>
                        <span className="font-medium">{selectedDevice.ip_address}</span>
                      </div>
                    )}
                    {selectedDevice.mac_address && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('iot.detailMacAddress')}</span>
                        <span className="font-medium">{selectedDevice.mac_address}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('iot.detailProtocol')}</span>
                      <span className="font-medium capitalize">
                        {protocolOptions.find(p => p.value === selectedDevice.communication_protocol)?.label}
                      </span>
                    </div>
                    {selectedDevice.operating_system && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{t('iot.detailOs')}</span>
                        <span className="font-medium">{selectedDevice.operating_system}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Status and Health */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('iot.detailSectionStatus')}</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('iot.detailStatus')}</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedDevice.status)}`}>
                        {statusOptions.find(s => s.value === selectedDevice.status)?.label}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('iot.detailSectionLocation')}</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('iot.detailCompany')}</span>
                      <span className="font-medium">{selectedDevice.company?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('iot.detailLocation')}</span>
                      <span className="font-medium">{selectedDevice.location?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('iot.detailInstallDate')}</span>
                      <span className="font-medium">{new Date(selectedDevice.installation_date).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('iot.detailAdmin')}</span>
                      <span className="font-medium">{selectedDevice.assigned_admin?.name || t('iot.detailUnassigned')}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sensor Readings */}
              {selectedDevice.sensor_readings && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('iot.detailSectionSensors')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {selectedDevice.sensor_readings.temperature && (
                      <div className="bg-red-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-red-700">{t('iot.detailTemperature')}</span>
                          <Thermometer className="w-5 h-5 text-red-600" />
                        </div>
                        <div className="text-2xl font-bold text-red-600">
                          {selectedDevice.sensor_readings.temperature}°C
                        </div>
                      </div>
                    )}

                    {selectedDevice.sensor_readings.humidity && (
                      <div className="bg-primary/10 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-primary-dark">{t('iot.detailHumidity')}</span>
                          <Droplets className="w-5 h-5 text-primary" />
                        </div>
                        <div className="text-2xl font-bold text-primary">
                          {selectedDevice.sensor_readings.humidity}%
                        </div>
                      </div>
                    )}

                    {selectedDevice.sensor_readings.pressure && (
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-purple-700">{t('iot.detailPressure')}</span>
                          <Gauge className="w-5 h-5 text-purple-600" />
                        </div>
                        <div className="text-2xl font-bold text-purple-600">
                          {selectedDevice.sensor_readings.pressure} hPa
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedDevice.notes && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('iot.detailSectionNotes')}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    {selectedDevice.notes}
                  </p>
                </div>
              )}

              {/* Timestamps */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  <span>{t('iot.detailCreatedAt')}</span>
                  <div className="font-medium">{new Date(selectedDevice.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}</div>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  <span>{t('iot.detailUpdatedAt')}</span>
                  <div className="font-medium">{new Date(selectedDevice.updated_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}</div>
                </div>
              </div>
            </div>
          </div>
          )}
        </Modal>

      {/* Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 right-4 z-10 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={selectedImage}
              alt="IoT Cihaz Görseli"
              className="w-full h-full object-contain rounded-lg"
            />
          </div>
        </div>
      )}

      {/* Bulk Label Modal */}
      <Modal isOpen={showBulkLabelModal} onClose={() => setShowBulkLabelModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <QrCode className="w-6 h-6 text-purple-600" />
                {t('iot.bulkModalTitle')}
              </h2>
              <button
                onClick={() => {
                  setShowBulkLabelModal(false);
                  setSelectedDevices([]);
                  setShowBulkLabelPreview(false);
                }}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {!showBulkLabelPreview ? (
                <>
                  {/* Filters */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{t('iot.bulkFilters')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('iot.bulkFilterLocation')}</label>
                        <select
                          value={bulkLabelFilters.location}
                          onChange={(e) => setBulkLabelFilters({...bulkLabelFilters, location: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="all">{t('iot.bulkAllLocations')}</option>
                          {locationsData.map(location => (
                            <option key={location.id} value={location.id}>{location.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('iot.bulkFilterStatus')}</label>
                        <select
                          value={bulkLabelFilters.status}
                          onChange={(e) => setBulkLabelFilters({...bulkLabelFilters, status: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="all">{t('iot.bulkAllStatuses')}</option>
                          <option value="online">{t('iot.statusOnline')}</option>
                          <option value="offline">{t('iot.statusOffline')}</option>
                          <option value="maintenance">{t('iot.statusMaintenance')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('iot.bulkFilterType')}</label>
                        <select
                          value={bulkLabelFilters.type}
                          onChange={(e) => setBulkLabelFilters({...bulkLabelFilters, type: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="all">{t('iot.bulkAllTypes')}</option>
                          {deviceTypeOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Device Selection */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {t('iot.bulkDeviceSelection', { count: selectedDevices.length })}
                      </h3>
                      <div className="flex gap-2">
                        <button
                          onClick={selectAllDevices}
                          className="px-3 py-1 text-sm bg-primary/20 text-primary-dark rounded-lg hover:bg-primary/30"
                        >
                          {t('iot.bulkSelectAll')}
                        </button>
                        <button
                          onClick={deselectAllDevices}
                          className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 dark:bg-gray-600"
                        >
                          {t('iot.bulkDeselectAll')}
                        </button>
                      </div>
                    </div>

                    <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-4">
                        {filteredDevicesForLabel.map(device => {
                          const TypeIcon = getDeviceTypeIcon(device.device_type);
                          return (
                            <div
                              key={device.id}
                              className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                selectedDevices.includes(device.id)
                                  ? 'border-purple-500 bg-purple-50'
                                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:border-gray-600'
                              }`}
                              onClick={() => toggleDeviceSelection(device.id)}
                            >
                              <div className="flex items-center gap-3">
                                <input
                                  type="checkbox"
                                  checked={selectedDevices.includes(device.id)}
                                  onChange={() => toggleDeviceSelection(device.id)}
                                  className="rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500"
                                />
                                <TypeIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                    {device.device_name}
                                  </div>
                                  <div className="text-sm text-gray-500 dark:text-gray-400">
                                    {device.brand} {device.model}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowBulkLabelModal(false)}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      onClick={() => setShowBulkLabelPreview(true)}
                      disabled={selectedDevices.length === 0}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <QrCode className="h-4 w-4" />
                      {t('iot.bulkPreviewBtn', { count: selectedDevices.length })}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Preview */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                      {t('iot.bulkPreviewTitle', { count: selectedDevices.length, pages: Math.ceil(selectedDevices.length / 4) })}
                    </h3>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      {t('iot.bulkPreviewDesc')}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowBulkLabelPreview(false)}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300"
                    >
                      {t('common.back')}
                    </button>
                    <button
                      onClick={printBulkLabels}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                    >
                      <Printer className="h-4 w-4" />
                      {t('iot.bulkPrint')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </Modal>
    </div>
  );
};

export default IoTDevicesPage;